import { Assert, Deferred, Value, pipe } from '@helo/utils';
import { DataTypeSelection } from '../DateTypeConfig/DataTypeSelection';
import { Filter } from '../shared/filter';
import { ContentNodeText } from './ContentNodeText';
const isRecord = (data) => {
    return typeof data === 'object' && data !== null;
};
const doesDataMatchConfig = (data, config) => {
    if (data === null || data === undefined)
        return true;
    if (config.kind === 'OBJECT' || config.kind === 'INPUT_OBJECT') {
        return isRecord(data);
    }
    if (config.kind === 'LIST') {
        return Array.isArray(data);
    }
    if (config.kind === 'UNION') {
        return config.possibleTypes.some(type => doesDataMatchConfig(data, type));
    }
    return true;
};
export const createContentNode = (config, inputData, unselectedConfig) => {
    const matchesConfig = doesDataMatchConfig(inputData, config);
    const data = inputData;
    if (!matchesConfig) {
        const dataDetails = isRecord(data)
            ? `Object keys: ${Object.keys(data).join(', ')}`
            : `Type: ${typeof data}`;
        throw new Error(`Cannot create content node. Data does not match config. Kind: ${config.kind}. Data details: ${dataDetails}`);
    }
    switch (config.kind) {
        case 'OBJECT': {
            const fields = config.fields.map(field => {
                const fieldData = data?.[field.key];
                const unselectedField = unselectedConfig && unselectedConfig.kind === 'OBJECT'
                    ? unselectedConfig.fields.find(f => f.key === field.key)
                    : undefined;
                return createContentNode(field, fieldData, unselectedField);
            });
            return {
                ...config,
                getDataTypeConfig: () => unselectedConfig || config,
                state: config.state,
                data,
                fields,
            };
        }
        case 'FIELD': {
            const unselectedOfType = unselectedConfig && unselectedConfig.kind === 'FIELD'
                ? unselectedConfig.ofType
                : undefined;
            return {
                ...config,
                getDataTypeConfig: () => unselectedConfig || config,
                state: config.state,
                data,
                ofType: createContentNode(config.ofType, data, unselectedOfType),
            };
        }
        case 'LIST': {
            const unselectedOfType = unselectedConfig && unselectedConfig.kind === 'LIST'
                ? unselectedConfig.ofType
                : undefined;
            const items = data === null || data === undefined
                ? data
                : Array.isArray(data)
                    ? data.map(item => createContentNode(config.ofType, item, unselectedOfType))
                    : undefined;
            const ofType = createContentNode(config.ofType, null, unselectedOfType);
            return {
                ...config,
                getDataTypeConfig: () => unselectedConfig || config,
                state: config.state,
                data,
                items,
                ofType: ofType,
            };
        }
        case 'UNION': {
            const foundType = config.possibleTypes.find(possibleType => typeof data === 'object' &&
                data !== null &&
                '__typename' in data &&
                data.__typename === possibleType.typeName);
            if (!foundType) {
                throw new Error(`Union type does not match data: ${JSON.stringify(data)}`);
            }
            const unselectedFoundType = unselectedConfig && unselectedConfig.kind === 'UNION'
                ? unselectedConfig.possibleTypes.find(possibleType => possibleType.typeName === foundType.typeName)
                : undefined;
            return createContentNode(foundType, data, unselectedFoundType);
        }
        case 'SCALAR': {
            return {
                ...config,
                getDataTypeConfig: () => unselectedConfig || config,
                state: config.state,
                data,
            };
        }
        case 'ENUM': {
            const enumValueConfig = config.enumValues.find(enumValue => enumValue.name?.toLowerCase() === data?.toLowerCase());
            if (!enumValueConfig) {
                return {
                    ...config,
                    getDataTypeConfig: () => unselectedConfig || config,
                    state: config.state,
                    data,
                    contentNode: undefined,
                };
            }
            const unselectedEnumValue = unselectedConfig && unselectedConfig.kind === 'ENUM'
                ? unselectedConfig.enumValues.find(ev => ev.name === enumValueConfig.name)
                : undefined;
            return {
                ...config,
                getDataTypeConfig: () => unselectedConfig || config,
                state: config.state,
                data,
                contentNode: createContentNode(enumValueConfig, data, unselectedEnumValue),
            };
        }
        case 'ENUM_VALUE': {
            return {
                ...config,
                getDataTypeConfig: () => unselectedConfig || config,
                state: config.state,
                data,
            };
        }
        case 'INPUT_OBJECT': {
            const fields = config.fields.map(inputField => {
                const inputFieldData = data?.[inputField.key];
                const unselectedInputField = unselectedConfig && unselectedConfig.kind === 'INPUT_OBJECT'
                    ? unselectedConfig.fields.find(f => f.key === inputField.key)
                    : undefined;
                return createContentNode(inputField, inputFieldData, unselectedInputField);
            });
            return {
                ...config,
                getDataTypeConfig: () => unselectedConfig || config,
                state: config.state,
                data,
                fields,
            };
        }
        case 'INPUT_VALUE': {
            const unselectedOfType = unselectedConfig && unselectedConfig.kind === 'INPUT_VALUE'
                ? unselectedConfig.ofType
                : undefined;
            return {
                ...config,
                getDataTypeConfig: () => unselectedConfig || config,
                state: config.state,
                data,
                ofType: createContentNode(config.ofType, data, unselectedOfType),
            };
        }
        default: {
            break;
        }
    }
    return config;
};
export const createContentNodeWithSelection = (config, inputData) => {
    const selection = DataTypeSelection.createSelectionFromValue(inputData);
    const selectedConfig = DataTypeSelection.getSelection(selection)(config);
    return createContentNode(selectedConfig, inputData, config);
};
export const createInputValueContentNodePreservingInputObjectFields = (config, inputData) => {
    if (config.ofType?.kind !== 'INPUT_OBJECT') {
        return createContentNodeWithSelection(config, inputData);
    }
    const inputObjectConfig = config.ofType;
    const data = inputData;
    const fields = inputObjectConfig.fields.map(fieldConfig => createContentNodeWithSelection(fieldConfig, data?.[fieldConfig.key]));
    const ofType = {
        ...inputObjectConfig,
        getDataTypeConfig: () => inputObjectConfig,
        state: inputObjectConfig.state,
        data,
        fields,
    };
    return {
        ...config,
        getDataTypeConfig: () => config,
        state: config.state,
        data: inputData,
        ofType,
    };
};
export const applyParentReferences = (contentNode, parentContentNode) => {
    let currentNode = Object.assign({}, contentNode, {
        get parent() {
            return parentContentNode;
        },
    });
    switch (currentNode.kind) {
        case 'OBJECT':
            currentNode.fields = currentNode.fields?.map(field => applyParentReferences(field, currentNode));
            break;
        case 'LIST':
            currentNode.items = currentNode.items?.map(item => applyParentReferences(item, currentNode));
            break;
        case 'FIELD':
            currentNode.ofType = applyParentReferences(currentNode.ofType, currentNode);
            break;
        case 'ENUM':
            currentNode.contentNode = applyParentReferences(currentNode.contentNode, currentNode);
            break;
        case 'INPUT_OBJECT':
            currentNode.fields = currentNode.fields?.map(field => applyParentReferences(field, currentNode));
            break;
        case 'INPUT_VALUE':
            currentNode.ofType = applyParentReferences(currentNode.ofType, currentNode);
            break;
        default:
            break;
    }
    return currentNode;
};
const doesContentNodeMatchMatcher = (contentNode, matcher) => {
    const [matcherType, matcherValue] = matcher;
    if (!contentNode) {
        return false;
    }
    switch (matcherType) {
        case 'key':
            return 'key' in contentNode && contentNode.key === matcherValue;
        case 'typeName':
            return 'typeName' in contentNode && contentNode.typeName === matcherValue;
        case 'kind':
            return (contentNode.kind.toUpperCase() ===
                matcherValue.toUpperCase());
        case 'dataMatch':
            return Filter.isFilterMatch(matcherValue)(contentNode.data);
        default:
            return false;
    }
};
const getMatcherCacheKey = ([matcherType, matcherValue]) => {
    const value = typeof matcherValue === 'string' || typeof matcherValue === 'number'
        ? matcherValue
        : JSON.stringify(matcherValue);
    return `${matcherType}:${value}`;
};
export const getContentNodeLocatorCacheKey = (locator) => locator.map(getMatcherCacheKey).join(',');
export const getContentNodeByExtractor = (nodesToSearch, extractor) => {
    let currentValue = nodesToSearch;
    for (const extractorPart of extractor) {
        if (currentValue === null || currentValue === undefined) {
            return null;
        }
        if (typeof extractorPart === 'number') {
            if (!Array.isArray(currentValue)) {
                return null;
            }
            currentValue = currentValue[extractorPart];
            continue;
        }
        if (!isRecord(currentValue)) {
            return null;
        }
        currentValue = currentValue[extractorPart];
    }
    return isRecord(currentValue) && 'kind' in currentValue
        ? currentValue
        : null;
};
const findContentNode = (locator) => (contentNode) => {
    if (!locator || !locator.length || !contentNode)
        return null;
    const [currentMatcher, ...remainingMatchers] = locator;
    if (doesContentNodeMatchMatcher(contentNode, currentMatcher)) {
        if (remainingMatchers.length === 0) {
            return contentNode;
        }
        if (contentNode.kind === 'OBJECT' && contentNode.fields) {
            for (const field of contentNode.fields) {
                const result = findContentNode(remainingMatchers)(field);
                if (result)
                    return result;
            }
        }
        if (contentNode.kind === 'INPUT_OBJECT' && contentNode.fields) {
            for (const field of contentNode.fields) {
                const result = findContentNode(remainingMatchers)(field);
                if (result)
                    return result;
            }
        }
        if (contentNode.kind === 'LIST' && contentNode.items) {
            const result = findContentNode(remainingMatchers)(contentNode.items[0]);
            if (result)
                return result;
        }
        if ((contentNode.kind === 'FIELD' || contentNode.kind === 'INPUT_VALUE') &&
            contentNode.ofType) {
            const result = findContentNode(remainingMatchers)(contentNode.ofType);
            if (result)
                return result;
        }
    }
    if (contentNode.kind === 'OBJECT' && contentNode.fields) {
        for (const field of contentNode.fields) {
            const result = findContentNode(locator)(field);
            if (result)
                return result;
        }
    }
    if (contentNode.kind === 'INPUT_OBJECT' && contentNode.fields) {
        for (const field of contentNode.fields) {
            const result = findContentNode(locator)(field);
            if (result)
                return result;
        }
    }
    if (contentNode.kind === 'LIST' && contentNode.items) {
        for (const item of contentNode.items) {
            const result = findContentNode(locator)(item);
            if (result)
                return result;
        }
    }
    if ((contentNode.kind === 'FIELD' || contentNode.kind === 'INPUT_VALUE') &&
        contentNode.ofType) {
        const result = findContentNode(locator)(contentNode.ofType);
        if (result)
            return result;
    }
    return null;
};
const getChildProviders = (parentProviders, childMatcher) => {
    const childProviders = [];
    const allTypesProvider = parentProviders.find(p => p.typeName === 'ALLTYPES');
    parentProviders.forEach(provider => {
        if (provider.typeName === 'ALLTYPES')
            return;
        if (childMatcher.propName === 'fields') {
            if (childMatcher.key && 'fields' in provider && provider.fields) {
                const matchingField = provider.fields.find(f => 'key' in f && f.key === childMatcher.key);
                if (matchingField)
                    childProviders.push(matchingField);
            }
        }
        else if (childMatcher.propName === 'ofType' && 'ofType' in provider) {
            if (provider.ofType) {
                childProviders.push(provider.ofType);
            }
        }
        else if (childMatcher.propName === 'items' && 'ofType' in provider) {
            if (provider.ofType) {
                childProviders.push(provider.ofType);
            }
        }
        else if (childMatcher.propName === 'contentNode' &&
            'contentNode' in provider) {
            if (provider.contentNode) {
                childProviders.push(provider.contentNode);
            }
        }
    });
    if (allTypesProvider) {
        childProviders.push(allTypesProvider);
    }
    return childProviders;
};
export const deeplySetState = (state) => (contentNode) => {
    if (!contentNode || !state || Object.keys(state).length === 0) {
        return contentNode;
    }
    const seen = new WeakMap();
    const visit = (node) => {
        const cached = seen.get(node);
        if (cached)
            return cached;
        const updated = {
            ...node,
            state: Deferred.merge(state)(node.state ?? {}),
        };
        seen.set(node, updated);
        if ('fields' in updated && Array.isArray(updated.fields)) {
            ;
            updated.fields =
                updated.fields.map(field => visit(field));
        }
        if ('args' in updated && Array.isArray(updated.args)) {
            ;
            updated.args = updated.args.map(arg => visit(arg));
        }
        if ('ofType' in updated && updated.ofType) {
            ;
            updated.ofType = visit(updated.ofType);
        }
        if ('items' in updated && Array.isArray(updated.items)) {
            ;
            updated.items = updated.items.map(item => visit(item));
        }
        if ('contentNode' in updated && updated.contentNode) {
            ;
            updated.contentNode = visit(updated.contentNode);
        }
        if ('enumValues' in updated && Array.isArray(updated.enumValues)) {
            ;
            updated.enumValues = updated.enumValues.map(enumValue => visit(enumValue));
        }
        if ('possibleTypes' in updated && Array.isArray(updated.possibleTypes)) {
            ;
            updated.possibleTypes = updated.possibleTypes.map((possibleType) => visit(possibleType));
        }
        return updated;
    };
    return visit(contentNode);
};
export const setContentNodeStates = (stateProviders) => (contentNode) => {
    if (!contentNode || !stateProviders || stateProviders.length === 0)
        return contentNode;
    const matchingProviders = stateProviders.filter(provider => {
        if (provider.typeName === 'ALLTYPES')
            return true;
        if (contentNode.kind === 'FIELD' &&
            'key' in provider &&
            'key' in contentNode) {
            return provider.key === contentNode.key;
        }
        if ('typeName' in provider && 'typeName' in contentNode) {
            return provider.typeName === contentNode.typeName;
        }
        return true;
    });
    if (matchingProviders.length === 0)
        return contentNode;
    const sortedProviders = [
        ...matchingProviders.filter(p => p.typeName === 'ALLTYPES'),
        ...matchingProviders.filter(p => p.typeName !== 'ALLTYPES'),
    ];
    const mergedState = sortedProviders.reduce((acc, provider) => Deferred.merge(provider.state ?? {})(acc), {});
    let updatedNode = {
        ...contentNode,
        state: Deferred.merge(mergedState)(contentNode.state ?? {}),
    };
    if (updatedNode.kind === 'OBJECT' && updatedNode.fields) {
        updatedNode.fields = updatedNode.fields.map(field => {
            const fieldKey = 'key' in field ? field.key : undefined;
            const childProviders = getChildProviders(matchingProviders, {
                propName: 'fields',
                key: fieldKey,
            });
            return setContentNodeStates(childProviders)(field);
        });
    }
    else if (updatedNode.kind === 'INPUT_OBJECT' && updatedNode.fields) {
        updatedNode.fields = updatedNode.fields.map(field => {
            const fieldKey = 'key' in field ? field.key : undefined;
            const childProviders = getChildProviders(matchingProviders, {
                propName: 'fields',
                key: fieldKey,
            });
            return setContentNodeStates(childProviders)(field);
        });
    }
    else if (updatedNode.kind === 'LIST') {
        if (updatedNode.ofType) {
            const ofTypeProviders = getChildProviders(matchingProviders, {
                propName: 'ofType',
            });
            updatedNode.ofType = setContentNodeStates(ofTypeProviders)(updatedNode.ofType);
        }
        if (updatedNode.items) {
            const itemProviders = getChildProviders(matchingProviders, {
                propName: 'ofType',
            });
            updatedNode.items = updatedNode.items.map(item => setContentNodeStates(itemProviders)(item));
        }
    }
    else if ((updatedNode.kind === 'FIELD' || updatedNode.kind === 'INPUT_VALUE') &&
        updatedNode.ofType) {
        const ofTypeProviders = getChildProviders(matchingProviders, {
            propName: 'ofType',
        });
        updatedNode.ofType = setContentNodeStates(ofTypeProviders)(updatedNode.ofType);
    }
    else if (updatedNode.kind === 'ENUM' && updatedNode.contentNode) {
        const contentNodeProviders = getChildProviders(matchingProviders, {
            propName: 'contentNode',
        });
        updatedNode.contentNode = setContentNodeStates(contentNodeProviders)(updatedNode.contentNode);
    }
    return updatedNode;
};
export const findContentNodeBreathFirst = (locator) => (nodesToSearch) => {
    if (!locator || locator.length === 0) {
        return null;
    }
    const initialNodes = Array.isArray(nodesToSearch)
        ? nodesToSearch
        : [nodesToSearch];
    const searchChildren = (currentNode, matcherIndex, searchFieldOfTypeOnMiss) => {
        switch (currentNode.kind) {
            case 'OBJECT':
            case 'INPUT_OBJECT':
                for (const field of currentNode.fields) {
                    const result = searchNode(field, matcherIndex, false);
                    if (result)
                        return result;
                }
                return null;
            case 'LIST': {
                const firstItem = currentNode.items?.[0];
                const child = firstItem ?? currentNode.ofType;
                return child ? searchNode(child, matcherIndex, false) : null;
            }
            case 'FIELD':
            case 'INPUT_VALUE':
                return searchFieldOfTypeOnMiss && currentNode.ofType
                    ? searchNode(currentNode.ofType, matcherIndex, false)
                    : null;
            case 'ENUM':
                return currentNode.contentNode
                    ? searchNode(currentNode.contentNode, matcherIndex, false)
                    : null;
            case 'ENUM_VALUE':
            case 'SCALAR':
            default:
                return null;
        }
    };
    const searchNode = (currentNode, matcherIndex, isInitialNode) => {
        if (doesContentNodeMatchMatcher(currentNode, locator[matcherIndex])) {
            if (matcherIndex === locator.length - 1) {
                return currentNode;
            }
            return searchChildren(currentNode, matcherIndex + 1, true);
        }
        return searchChildren(currentNode, matcherIndex, isInitialNode);
    };
    for (const node of initialNodes) {
        if (node) {
            const result = searchNode(node, 0, true);
            if (result)
                return result;
        }
    }
    return null;
};
const compileMatcher = (matcher) => {
    const [matcherType, matcherValue] = matcher;
    if (matcherType === 'kind') {
        return {
            type: 'kind',
            value: matcherValue.toUpperCase(),
            isKey: false,
        };
    }
    if (matcherType === 'dataMatch') {
        return {
            type: 'dataMatch',
            value: matcherValue,
            isKey: false,
            predicate: Filter.isFilterMatch(matcherValue),
        };
    }
    return {
        type: matcherType,
        value: matcherValue,
        isKey: matcherType === 'key',
    };
};
const matchesCompiledMatcher = (contentNode, compiled) => {
    if (!contentNode)
        return false;
    switch (compiled.type) {
        case 'key':
            return ('key' in contentNode &&
                contentNode.key === compiled.value);
        case 'typeName':
            return ('typeName' in contentNode &&
                contentNode.typeName === compiled.value);
        case 'kind':
            return contentNode.kind === compiled.value;
        case 'dataMatch':
            return compiled.predicate(contentNode.data);
        default:
            return false;
    }
};
export const findContentNodeCached = (locator, extractorCache, dataCache) => (nodesToSearch) => {
    if (!locator || locator.length === 0) {
        return null;
    }
    const initialNodes = Array.isArray(nodesToSearch)
        ? nodesToSearch
        : [nodesToSearch];
    if (initialNodes.length === 0) {
        return null;
    }
    const locatorLength = locator.length;
    const compiledMatchers = new Array(locatorLength);
    const cumulativeCacheKeys = new Array(locatorLength);
    let runningCacheKey = '';
    for (let i = 0; i < locatorLength; i++) {
        const matcher = locator[i];
        compiledMatchers[i] = compileMatcher(matcher);
        const segmentKey = getMatcherCacheKey(matcher);
        runningCacheKey =
            runningCacheKey === '' ? segmentKey : `${runningCacheKey},${segmentKey}`;
        cumulativeCacheKeys[i] = runningCacheKey;
    }
    let cachedNode = null;
    let cachedMatcherIndex = 0;
    let cachedExtractor = null;
    if (dataCache) {
        for (let length = locatorLength; length > 0; length--) {
            const node = dataCache[cumulativeCacheKeys[length - 1]];
            if (node &&
                matchesCompiledMatcher(node, compiledMatchers[length - 1])) {
                cachedNode = node;
                cachedMatcherIndex = length;
                break;
            }
        }
    }
    if (extractorCache) {
        for (let length = locatorLength; length > cachedMatcherIndex; length--) {
            const cacheKey = cumulativeCacheKeys[length - 1];
            const extractorForLength = extractorCache[cacheKey];
            if (!extractorForLength) {
                continue;
            }
            const node = getContentNodeByExtractor(nodesToSearch, extractorForLength);
            if (node &&
                matchesCompiledMatcher(node, compiledMatchers[length - 1])) {
                cachedNode = node;
                cachedMatcherIndex = length;
                cachedExtractor = extractorForLength;
                if (dataCache) {
                    dataCache[cacheKey] = node;
                }
                break;
            }
        }
    }
    if (cachedNode && cachedMatcherIndex === locatorLength) {
        return cachedNode;
    }
    const extractorCacheRef = extractorCache;
    const dataCacheRef = dataCache;
    const searchChildren = (currentNode, matcherIndex, extractor, searchFieldOfTypeOnMiss) => {
        switch (currentNode.kind) {
            case 'OBJECT':
            case 'INPUT_OBJECT': {
                const fields = currentNode.fields;
                const fieldsLength = fields.length;
                extractor?.push('fields');
                for (let i = 0; i < fieldsLength; i++) {
                    extractor?.push(i);
                    const result = searchNode(fields[i], matcherIndex, false, extractor);
                    if (result)
                        return result;
                    extractor?.pop();
                }
                extractor?.pop();
                return null;
            }
            case 'LIST': {
                const items = currentNode.items;
                const firstItem = items !== undefined && items !== null ? items[0] : undefined;
                if (firstItem) {
                    extractor?.push('items', 0);
                    const result = searchNode(firstItem, matcherIndex, false, extractor);
                    if (result)
                        return result;
                    extractor?.pop();
                    extractor?.pop();
                    return null;
                }
                if (currentNode.ofType) {
                    extractor?.push('ofType');
                    const result = searchNode(currentNode.ofType, matcherIndex, false, extractor);
                    if (result)
                        return result;
                    extractor?.pop();
                }
                return null;
            }
            case 'FIELD':
            case 'INPUT_VALUE':
                if (searchFieldOfTypeOnMiss && currentNode.ofType) {
                    extractor?.push('ofType');
                    const result = searchNode(currentNode.ofType, matcherIndex, false, extractor);
                    if (result)
                        return result;
                    extractor?.pop();
                }
                return null;
            case 'ENUM':
                if (currentNode.contentNode) {
                    extractor?.push('contentNode');
                    const result = searchNode(currentNode.contentNode, matcherIndex, false, extractor);
                    if (result)
                        return result;
                    extractor?.pop();
                }
                return null;
            case 'ENUM_VALUE':
            case 'SCALAR':
            default:
                return null;
        }
    };
    const searchNode = (currentNode, matcherIndex, isInitialNode, extractor) => {
        const compiled = compiledMatchers[matcherIndex];
        if (matchesCompiledMatcher(currentNode, compiled)) {
            const cacheKey = cumulativeCacheKeys[matcherIndex];
            if (extractorCacheRef && extractor) {
                extractorCacheRef[cacheKey] = extractor.slice();
            }
            if (dataCacheRef) {
                dataCacheRef[cacheKey] = currentNode;
            }
            if (matcherIndex === locatorLength - 1) {
                return currentNode;
            }
            return searchChildren(currentNode, matcherIndex + 1, extractor, true);
        }
        if (compiled.isKey &&
            !isInitialNode &&
            (currentNode.kind === 'FIELD' || currentNode.kind === 'INPUT_VALUE')) {
            return null;
        }
        return searchChildren(currentNode, matcherIndex, extractor, isInitialNode);
    };
    if (cachedNode) {
        const result = searchChildren(cachedNode, cachedMatcherIndex, cachedExtractor?.slice() ?? null, true);
        if (result) {
            return result;
        }
    }
    for (let i = 0; i < initialNodes.length; i++) {
        const node = initialNodes[i];
        if (node) {
            const result = searchNode(node, 0, true, [i]);
            if (result) {
                return result;
            }
        }
    }
    return null;
};
export const findAndUpdateChild = (locator, updator) => (rootNode) => {
    if (!locator || locator.length === 0) {
        return rootNode;
    }
    const queue = [];
    const initialNodeSet = new Set([rootNode]);
    const rootUpdateFn = (node) => node;
    queue.push([rootNode, 0, rootUpdateFn]);
    const enqueueNode = (node, matcherIndexToUse, parentUpdateFn) => {
        queue.push([node, matcherIndexToUse, parentUpdateFn]);
    };
    const createChildUpdateFn = (currentNode, parentUpdateFn, itemIndex) => (updatedChild) => {
        const newCurrentNode = itemIndex !== undefined
            ? updateChildContentNode(updatedChild, itemIndex)(currentNode)
            : updateChildContentNode(updatedChild)(currentNode);
        return parentUpdateFn(newCurrentNode);
    };
    const enqueueMatchedChildren = (currentNode, nextMatcherIndex, parentUpdateFn) => {
        if (currentNode.kind === 'OBJECT' && currentNode.fields) {
            for (const field of currentNode.fields) {
                if (field) {
                    enqueueNode(field, nextMatcherIndex, createChildUpdateFn(currentNode, parentUpdateFn));
                }
            }
        }
        if (currentNode.kind === 'INPUT_OBJECT' && currentNode.fields) {
            for (const inputField of currentNode.fields) {
                if (inputField) {
                    enqueueNode(inputField, nextMatcherIndex, createChildUpdateFn(currentNode, parentUpdateFn));
                }
            }
        }
        if (currentNode.kind === 'LIST') {
            const items = currentNode.items;
            if (!items || items.length === 0) {
                if (currentNode.ofType) {
                    enqueueNode(currentNode.ofType, nextMatcherIndex, createChildUpdateFn(currentNode, parentUpdateFn));
                }
            }
            if (Array.isArray(items)) {
                for (let itemIndex = 0; itemIndex < items.length; itemIndex++) {
                    const item = items[itemIndex];
                    if (item) {
                        enqueueNode(item, nextMatcherIndex, createChildUpdateFn(currentNode, parentUpdateFn, itemIndex));
                    }
                }
            }
        }
        if ((currentNode.kind === 'FIELD' || currentNode.kind === 'INPUT_VALUE') &&
            currentNode.ofType) {
            enqueueNode(currentNode.ofType, nextMatcherIndex, createChildUpdateFn(currentNode, parentUpdateFn));
        }
        if (currentNode.kind === 'ENUM' && currentNode.contentNode) {
            enqueueNode(currentNode.contentNode, nextMatcherIndex, createChildUpdateFn(currentNode, parentUpdateFn));
        }
    };
    while (queue.length > 0) {
        const [currentNode, matcherIndex, parentUpdateFn] = queue.shift();
        const currentMatcher = locator[matcherIndex];
        const isMatch = doesContentNodeMatchMatcher(currentNode, currentMatcher);
        if (isMatch) {
            if (matcherIndex === locator.length - 1) {
                const updatedChild = updator(currentNode);
                if (updatedChild !== currentNode) {
                    return parentUpdateFn(updatedChild);
                }
                return rootNode;
            }
            enqueueMatchedChildren(currentNode, matcherIndex + 1, parentUpdateFn);
        }
        else {
            const checkAndEnqueueChild = (child) => {
                const childMatches = doesContentNodeMatchMatcher(child, currentMatcher);
                const childUpdateFn = createChildUpdateFn(currentNode, parentUpdateFn);
                if (childMatches) {
                    if (matcherIndex === locator.length - 1) {
                        const updatedChild = updator(child);
                        if (updatedChild !== child) {
                            return childUpdateFn(updatedChild);
                        }
                        return rootNode;
                    }
                    enqueueMatchedChildren(child, matcherIndex + 1, childUpdateFn);
                }
                else {
                    enqueueNode(child, matcherIndex, childUpdateFn);
                }
                return null;
            };
            if (currentNode.kind === 'OBJECT' && currentNode.fields) {
                for (const field of currentNode.fields) {
                    if (field) {
                        const result = checkAndEnqueueChild(field);
                        if (result)
                            return result;
                    }
                }
            }
            if (currentNode.kind === 'INPUT_OBJECT' && currentNode.fields) {
                for (const inputField of currentNode.fields) {
                    if (inputField) {
                        const result = checkAndEnqueueChild(inputField);
                        if (result)
                            return result;
                    }
                }
            }
            if (currentNode.kind === 'LIST') {
                const items = currentNode.items;
                if (!items || items.length === 0) {
                    if (currentNode.ofType) {
                        enqueueNode(currentNode.ofType, matcherIndex, createChildUpdateFn(currentNode, parentUpdateFn));
                    }
                    const result = checkAndEnqueueChild(currentNode.ofType);
                    if (result)
                        return result;
                }
                if (Array.isArray(items)) {
                    for (let itemIndex = 0; itemIndex < items.length; itemIndex++) {
                        const item = items[itemIndex];
                        if (item) {
                            const result = checkAndEnqueueChild(item);
                            if (result)
                                return result;
                        }
                    }
                }
            }
            if (currentNode.kind === 'ENUM' && currentNode.contentNode) {
                const result = checkAndEnqueueChild(currentNode.contentNode);
                if (result)
                    return result;
            }
            if ((currentNode.kind === 'FIELD' ||
                currentNode.kind === 'INPUT_VALUE') &&
                currentNode.ofType &&
                initialNodeSet.has(currentNode)) {
                const result = checkAndEnqueueChild(currentNode.ofType);
                if (result)
                    return result;
            }
        }
    }
    return rootNode;
};
const updateChildContentNode = (newChild, index) => (contentNode) => {
    const isArray = Array.isArray(newChild);
    const isFunction = typeof newChild === 'function';
    switch (contentNode.kind) {
        case 'OBJECT': {
            if (isArray) {
                const newFields = newChild;
                const newData = {};
                for (const field of newFields) {
                    if ('key' in field) {
                        newData[field.key] = field.data;
                    }
                }
                return {
                    ...contentNode,
                    fields: newFields,
                    data: newData,
                };
            }
            if (isFunction) {
                const newFields = contentNode.fields.map(field => newChild(field));
                const newData = {};
                for (const field of newFields) {
                    if ('key' in field) {
                        newData[field.key] = field.data;
                    }
                }
                return {
                    ...contentNode,
                    fields: newFields,
                    data: newData,
                };
            }
            if (!('key' in newChild)) {
                return contentNode;
            }
            const newFields = contentNode.fields.map(field => field.key === newChild.key ? newChild : field);
            return {
                ...contentNode,
                fields: newFields,
                data: {
                    ...(contentNode.data || {}),
                    [newChild.key]: newChild.data,
                },
            };
        }
        case 'INPUT_OBJECT': {
            if (isArray) {
                const newInputFields = newChild;
                const newData = {};
                for (const field of newInputFields) {
                    if ('key' in field) {
                        newData[field.key] = field.data;
                    }
                }
                return {
                    ...contentNode,
                    fields: newInputFields,
                    data: newData,
                };
            }
            if (isFunction) {
                const newInputFields = contentNode.fields.map(field => newChild(field));
                const newData = {};
                for (const field of newInputFields) {
                    if ('key' in field) {
                        newData[field.key] = field.data;
                    }
                }
                return {
                    ...contentNode,
                    fields: newInputFields,
                    data: newData,
                };
            }
            if (!('key' in newChild)) {
                return contentNode;
            }
            const newInputFields = contentNode.fields.map(field => 'key' in field && field.key === newChild.key ? newChild : field);
            return {
                ...contentNode,
                fields: newInputFields,
                data: {
                    ...(contentNode.data || {}),
                    [newChild.key]: newChild.data,
                },
            };
        }
        case 'LIST': {
            if (isArray) {
                const newItems = newChild;
                return {
                    ...contentNode,
                    items: newItems,
                    data: newItems.map(item => item.data),
                };
            }
            if (isFunction) {
                const newItems = contentNode.items.map(item => newChild(item));
                return {
                    ...contentNode,
                    items: newItems,
                    data: newItems.map(item => item.data),
                };
            }
            if (index === undefined ||
                index < 0 ||
                index >= contentNode.items.length) {
                return contentNode;
            }
            const newItems = contentNode.items.map((item, i) => i === index ? newChild : item);
            return {
                ...contentNode,
                items: newItems,
                data: newItems.map(item => item.data),
            };
        }
        case 'FIELD': {
            if (isArray) {
                return contentNode;
            }
            if (isFunction) {
                const newOfType = newChild(contentNode.ofType);
                return {
                    ...contentNode,
                    ofType: newOfType,
                    data: newOfType.data,
                };
            }
            return {
                ...contentNode,
                ofType: newChild,
                data: newChild.data,
            };
        }
        case 'INPUT_VALUE': {
            if (isArray) {
                return contentNode;
            }
            if (isFunction) {
                const newOfType = newChild(contentNode.ofType);
                return {
                    ...contentNode,
                    data: newOfType.data,
                    ofType: newOfType,
                };
            }
            return {
                ...contentNode,
                data: newChild.data,
                ofType: newChild,
            };
        }
        case 'ENUM': {
            if (isArray) {
                const newEnumValues = newChild;
                const currentContentNodeName = 'name' in contentNode.contentNode
                    ? contentNode.contentNode.name
                    : undefined;
                const newContentNode = newEnumValues.find(ev => 'name' in ev && ev.name === currentContentNodeName) ||
                    newEnumValues[0] ||
                    contentNode.contentNode;
                return {
                    ...contentNode,
                    enumValues: newEnumValues,
                    contentNode: newContentNode,
                };
            }
            if (isFunction) {
                const newEnumValues = contentNode.enumValues.map(enumValue => newChild(enumValue));
                const currentContentNodeName = 'name' in contentNode.contentNode
                    ? contentNode.contentNode.name
                    : undefined;
                const newContentNode = newEnumValues.find(ev => 'name' in ev && ev.name === currentContentNodeName) ||
                    newEnumValues[0] ||
                    contentNode.contentNode;
                return {
                    ...contentNode,
                    enumValues: newEnumValues,
                    contentNode: newContentNode,
                };
            }
            if ('enumValues' in contentNode && 'name' in newChild) {
                const newEnumValues = contentNode.enumValues.map(enumValue => 'name' in enumValue && enumValue.name === newChild.name
                    ? newChild
                    : enumValue);
                const shouldUpdateContentNode = 'name' in contentNode.contentNode &&
                    'name' in newChild &&
                    contentNode.contentNode.name === newChild.name;
                return {
                    ...contentNode,
                    enumValues: newEnumValues,
                    contentNode: shouldUpdateContentNode
                        ? newChild
                        : contentNode.contentNode,
                };
            }
            return contentNode;
        }
        default:
            return contentNode;
    }
};
const addChildContentNode = (newChild) => (contentNode) => {
    switch (contentNode.kind) {
        case 'OBJECT': {
            if (!('key' in newChild)) {
                return contentNode;
            }
            const newFields = [...contentNode.fields, newChild];
            return {
                ...contentNode,
                fields: newFields,
                data: {
                    ...(contentNode.data || {}),
                    [newChild.key]: newChild.data,
                },
            };
        }
        case 'INPUT_OBJECT': {
            if (!('key' in newChild)) {
                return contentNode;
            }
            const newInputFields = [...contentNode.fields, newChild];
            return {
                ...contentNode,
                fields: newInputFields,
                data: {
                    ...(contentNode.data || {}),
                    [newChild.key]: newChild.data,
                },
            };
        }
        case 'LIST': {
            const newItems = [...contentNode.items, newChild];
            return {
                ...contentNode,
                items: newItems,
                data: newItems.map(item => item.data),
            };
        }
        case 'ENUM': {
            if ('enumValues' in contentNode && 'name' in newChild) {
                const newEnumValues = [
                    ...contentNode.enumValues,
                    newChild,
                ];
                return {
                    ...contentNode,
                    enumValues: newEnumValues,
                };
            }
            return contentNode;
        }
        default:
            return contentNode;
    }
};
const removeChildContentNode = (childToRemove) => (contentNode) => {
    switch (contentNode.kind) {
        case 'OBJECT': {
            if (typeof childToRemove === 'number' || !('key' in childToRemove)) {
                return contentNode;
            }
            const keyToRemove = childToRemove.key;
            const newFields = contentNode.fields.filter(f => f.key !== keyToRemove);
            const { [keyToRemove]: _, ...newData } = contentNode.data || {};
            return {
                ...contentNode,
                fields: newFields,
                data: newData,
            };
        }
        case 'INPUT_OBJECT': {
            if (typeof childToRemove === 'number' || !('key' in childToRemove)) {
                return contentNode;
            }
            const keyToRemove = childToRemove.key;
            const newInputFields = contentNode.fields.filter(f => !('key' in f) || f.key !== keyToRemove);
            const { [keyToRemove]: _, ...newData } = contentNode.data || {};
            return {
                ...contentNode,
                fields: newInputFields,
                data: newData,
            };
        }
        case 'LIST': {
            let indexToRemove;
            if (typeof childToRemove === 'number') {
                indexToRemove = childToRemove;
            }
            else {
                indexToRemove = contentNode.items.findIndex(item => item === childToRemove);
            }
            if (indexToRemove < 0 || indexToRemove >= contentNode.items.length) {
                return contentNode;
            }
            const newItems = contentNode.items.filter((_, i) => i !== indexToRemove);
            return {
                ...contentNode,
                items: newItems,
                data: newItems.map(item => item.data),
            };
        }
        case 'FIELD': {
            console.log('Cannot remove child from FIELD. To remove a field child, remove the field from its parent as they must exist together.');
            return contentNode;
        }
        case 'ENUM': {
            console.log('Cannot remove child from ENUM. To remove an enum child, remove the enum from its parent as they must exist together.');
            return contentNode;
        }
        default:
            return contentNode;
    }
};
const toDebugObject = (obj) => {
    if (obj === null || typeof obj !== 'object')
        return obj;
    if (Array.isArray(obj)) {
        return obj.map(item => toDebugObject(item));
    }
    const result = {};
    for (const key of Object.keys(obj)) {
        const value = obj[key];
        if (typeof value === 'function')
            continue;
        result[key] =
            value !== null && typeof value === 'object' ? toDebugObject(value) : value;
    }
    return result;
};
const getData = () => (contentNode) => contentNode?.data;
const updateContentNode = (data) => (contentNode) => {
    const selection = DataTypeSelection.createSelectionFromValue(data);
    const selectedConfig = DataTypeSelection.getSelection(selection)(contentNode.getDataTypeConfig());
    return createContentNode(selectedConfig, data, contentNode.getDataTypeConfig());
};
const updateParentContentNode = (parentContentNode) => (contentNode) => {
    return updateChildContentNode(contentNode)(parentContentNode);
};
const getDataTypeConfig = () => (contentNode) => contentNode.getDataTypeConfig();
const isKind = (kind) => (value) => {
    if (typeof value === 'object' &&
        value !== null &&
        'kind' in value &&
        value.kind === kind) {
        return true;
    }
    return false;
};
const hasFields = (node) => {
    return ((isKind('OBJECT')(node) || isKind('INPUT_OBJECT')(node)) &&
        'fields' in node &&
        Array.isArray(node.fields));
};
const getFields = () => (node) => pipe(node, Assert.orThrow(Value.isNotNullable), Assert.orThrow(hasFields), node => node.fields);
const hasOfType = (node) => {
    return ((isKind('FIELD')(node) ||
        isKind('INPUT_VALUE')(node) ||
        isKind('LIST')(node)) &&
        'ofType' in node &&
        node.ofType !== undefined &&
        node.ofType !== null);
};
const getOfType = () => (node) => pipe(node, Assert.orThrow(Value.isNotNullable), Assert.orThrow(hasOfType), node => node.ofType);
const hasItems = (node) => {
    return isKind('LIST')(node) && 'items' in node && Array.isArray(node.items);
};
const getItems = () => (node) => pipe(node, node => node.items);
const getContentNode = () => (node) => pipe(node, Assert.orThrow(Value.isNotNullable), Assert.orThrow(isKind('ENUM')), node => node.contentNode);
const hasKey = (node) => {
    return ((isKind('FIELD')(node) || isKind('INPUT_VALUE')(node)) &&
        'key' in node &&
        typeof node.key === 'string');
};
const getKey = () => (node) => pipe(node, Assert.orThrow(Value.isNotNullable), Assert.orThrow(hasKey), node => node.key);
const getState = () => (node) => pipe(node, Assert.orThrow(Value.isNotNullable), node => node.state);
const asText = (stateOverrides) => (node) => pipe(node, deeplySetState(stateOverrides ?? {}), ContentNodeText.asText(stateOverrides));
export const ContentNodeUtils = {
    findContentNode: findContentNodeCached,
    findContentNodeCached,
    findContentNodeBreathFirst,
    findAndUpdateChild,
    setState: setContentNodeStates,
    deeplySetState,
    createContentNode,
    updateContentNode,
    createContentNodeWithSelection,
    createInputValueContentNodePreservingInputObjectFields,
    updateChildContentNode,
    updateParentContentNode,
    addChildContentNode,
    removeChildContentNode,
    getData,
    getDataTypeConfig,
    getKey,
    getState,
    toDebugObject,
    isKind,
    getFields,
    getOfType,
    getItems,
    getContentNode,
    asText,
    resolvePrimaryValueContentNode: ContentNodeText.resolvePrimaryValueContentNode,
};
//# sourceMappingURL=ContentNode.utils.js.map